/**
 * Theme / density / language controls, shared by the TopBar menus and the settings pages of
 * both apps (one implementation, DESIGN.md §1.4).
 */
import { useI18n, type MessageKey } from '@platform/i18n';
import { LanguagesIcon, LogOutIcon, MonitorIcon, MoonIcon, Rows3Icon, Rows4Icon, SettingsIcon, SunIcon, SunMoonIcon, type LucideIcon } from 'lucide-react';
import { Button } from '../components/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/card';
import { SegmentedControl } from '../components/form-controls';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../components/dropdown-menu';
import { Avatar, AvatarFallback, initials } from '../components/misc';
import { usePreferences } from '../hooks/use-preferences';
import { DENSITIES, THEMES, type Density, type Theme } from '../lib/preferences';

const themeOptions: Record<Theme, { label: MessageKey; icon: LucideIcon }> = {
  system: { label: 'prefs.themeSystem', icon: MonitorIcon },
  light: { label: 'prefs.themeLight', icon: SunIcon },
  dark: { label: 'prefs.themeDark', icon: MoonIcon },
};

const densityOptions: Record<Density, { label: MessageKey; icon: LucideIcon }> = {
  compact: { label: 'prefs.densityCompact', icon: Rows4Icon },
  comfortable: { label: 'prefs.densityComfortable', icon: Rows3Icon },
};

/** TopBar quick toggle for theme and density (DESIGN.md §9.1). */
export function DisplayMenu() {
  const { t } = useI18n();
  const { theme, density, setTheme, setDensity } = usePreferences();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={t('shell.display')}>
          <SunMoonIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>{t('prefs.theme')}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={theme} onValueChange={(value) => setTheme(value as Theme)}>
          {THEMES.map((option) => (
            <DropdownMenuRadioItem key={option} value={option}>
              {t(themeOptions[option].label)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('prefs.density')}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={density} onValueChange={(value) => setDensity(value as Density)}>
          {DENSITIES.map((option) => (
            <DropdownMenuRadioItem key={option} value={option}>
              {t(densityOptions[option].label)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export interface UserMenuProps {
  name: string;
  email: string;
  onSignOut: () => void;
  settingsAction?: { label: string; onSelect: () => void };
  onLocaleChange?: (locale: string) => void;
  languageDisabled?: boolean;
}

/** TopBar user menu: identity, display preferences (theme, density), language, sign out. */
export function UserMenu({ name, email, onSignOut, settingsAction, onLocaleChange, languageDisabled = false }: UserMenuProps) {
  const { t, locale, languages, setLocale } = useI18n();
  const { theme, density, setTheme, setDensity } = usePreferences();
  const ThemeIcon = themeOptions[theme].icon;
  const DensityIcon = densityOptions[density].icon;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-full" aria-label={t('shell.userMenu')}>
          <Avatar className="size-7">
            <AvatarFallback>{initials(name)}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex flex-col gap-0.5 font-normal">
          <span className="truncate text-sm font-medium text-foreground">{name}</span>
          <span className="truncate">{email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {settingsAction && (
          <DropdownMenuItem onSelect={settingsAction.onSelect}>
            <SettingsIcon aria-hidden />
            {settingsAction.label}
          </DropdownMenuItem>
        )}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <ThemeIcon />
            {t('prefs.theme')}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup value={theme} onValueChange={(value) => setTheme(value as Theme)}>
              {THEMES.map((option) => (
                <DropdownMenuRadioItem key={option} value={option}>
                  {t(themeOptions[option].label)}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <DensityIcon />
            {t('prefs.density')}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup value={density} onValueChange={(value) => setDensity(value as Density)}>
              {DENSITIES.map((option) => (
                <DropdownMenuRadioItem key={option} value={option}>
                  {t(densityOptions[option].label)}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger disabled={languageDisabled}>
            <LanguagesIcon />
            {t('prefs.language')}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup value={locale} onValueChange={onLocaleChange ?? setLocale}>
              {languages.map((language) => (
                <DropdownMenuRadioItem key={language.locale} value={language.locale} lang={language.locale}>
                  {language.name}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onSignOut}>
          <LogOutIcon />
          {t('auth.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Settings card with all display preferences (DESIGN.md T5). */
export function PreferencesCard() {
  const { t, locale, languages, setLocale } = useI18n();
  const { theme, density, setTheme, setDensity } = usePreferences();
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('prefs.title')}</CardTitle>
        <CardDescription>{t('prefs.description')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <SegmentedControl
          label={t('prefs.theme')}
          value={theme}
          onChange={setTheme}
          options={THEMES.map((option) => ({ value: option, label: t(themeOptions[option].label), icon: themeOptions[option].icon }))}
        />
        <SegmentedControl
          label={t('prefs.density')}
          value={density}
          onChange={setDensity}
          options={DENSITIES.map((option) => ({ value: option, label: t(densityOptions[option].label), icon: densityOptions[option].icon }))}
        />
        <SegmentedControl
          label={t('prefs.language')}
          value={locale}
          onChange={setLocale}
          options={languages.map((language) => ({ value: language.locale, label: language.name }))}
        />
      </CardContent>
    </Card>
  );
}
